import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'

import { AppButton, AppDivider, AppForm, AppGroup } from 'client/ui-building-blocks'
import { ValidationErrorCode } from 'cat-shared/utils'
import { FormName, RecoverPasswordFormValues } from 'client/utils'
import { useAppContext, usePageStateContext } from 'client/hooks'
import { useGetRecoveryLink, useRecoverPassword } from 'client/api'

import {
  parseApiErrors,
  startRecoverEmailSocket,
  killRecoverEmailSocket,
  displaySnackBar,
  displayFormValidationError,
} from 'client/services'

import EmailInput from './fields/email-input'
import NewPasswordInput from './fields/new-password-input'

const RecoverPasswordForm = () => {
  const [apiErrors, setApiErrors] = useState(null)

  const { t } = useTranslation()
  const { recoverPassToken, setRecoverPassToken } = useAppContext()
  const { setAuthForm } = usePageStateContext()

  const {
    mutateAsync: getRecoveryLink,
    isLoading: isGettingRecoveryLink,
    isSuccess: getRecoveryLinkSuccess,
  } = useGetRecoveryLink()

  const { mutateAsync: recoverPassword, isLoading: isRecoveringPassword } =
    useRecoverPassword()

  const handleGetRecoveryLink = (values: RecoverPasswordFormValues) => {
    return getRecoveryLink(values).then(data => {
      startRecoverEmailSocket(values.email, setRecoverPassToken)
      if (data?.msg) {
        displaySnackBar(data.msg)
      }
    })
  }

  const handleRecoverPassword = (formValues: RecoverPasswordFormValues) => {
    return recoverPassword({ formValues, token: recoverPassToken }).then(data => {
      setRecoverPassToken('')
      setAuthForm(FormName.AuthenticationForm)
      if (data?.msg) {
        displaySnackBar(data.msg)
      }
    })
  }

  const form = useForm<RecoverPasswordFormValues, undefined, RecoverPasswordFormValues>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      newPassword: '',
    },
  })

  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = form

  const onClearClick = () => {
    reset()
    setApiErrors(null)
  }

  const onCloseClick = () => {
    killRecoverEmailSocket()
    setRecoverPassToken('')
    setAuthForm(FormName.AuthenticationForm)
    window.scrollTo(0, 0)
  }

  const handleApiError = err => {
    if (err?.valErr) {
      setApiErrors(parseApiErrors(err?.valErr))
      if (err.valErr) {
        Object.keys(err.valErr).forEach(fieldName => {
          displayFormValidationError(
            fieldName,
            null,
            err.valErr[fieldName].message as ValidationErrorCode
          )
        })
      }
    }
  }

  const isLoading = isGettingRecoveryLink || isRecoveringPassword

  return (
    <>
      <FormProvider {...form}>
        <AppForm
          onSubmit={handleSubmit(data => {
            return !recoverPassToken
              ? handleGetRecoveryLink(data).catch(handleApiError)
              : handleRecoverPassword(data).catch(handleApiError)
          })}
        >
          <EmailInput
            getRecoveryLinkSuccess={getRecoveryLinkSuccess}
            apiErrors={apiErrors}
            isLoading={isLoading}
          />
          <NewPasswordInput
            isLoading={isLoading}
            getRecoveryLinkSuccess={getRecoveryLinkSuccess}
          />
          <AppGroup fullWidth="mobile" alignment="verticalMobile">
            <AppButton
              variant="info"
              text={
                recoverPassToken
                  ? t('common.buttons.update')
                  : t('common.buttons.recover')
              }
              loading={isLoading}
              disabled={
                !isDirty ||
                !isEmpty(errors) ||
                (!recoverPassToken && getRecoveryLinkSuccess)
              }
              type="submit"
            />
            <AppButton
              variant="blank"
              text={t('common.buttons.clear')}
              onClick={onClearClick}
              disabled={
                isLoading || !isDirty || (!recoverPassToken && getRecoveryLinkSuccess)
              }
              type="reset"
            />
          </AppGroup>
        </AppForm>
      </FormProvider>
      <AppDivider border size="large" />
      <AppButton
        variant="success"
        text={t('common.buttons.back')}
        disabled={isLoading}
        onClick={onCloseClick}
        iconName="CHEVRON_LEFT"
        position="center"
      />
    </>
  )
}

export default RecoverPasswordForm
